import {
  Building2,
  FolderKanban,
  Link2,
  Plus,
  RefreshCw,
  Store,
  WalletCards,
  X,
} from "lucide-react";
import { FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, formatDate } from "../components/Badge";
import { Field, Input, Select } from "../components/Form";
import { Empty, ErrorState, Failure, Loading, Success } from "../components/State";
import { api, json } from "../lib/api";
import type { Currency, EnterpriseWorkspace } from "../lib/types";
import { useAsync } from "../lib/useAsync";

const blankForm = { name: "", legal_name: "", currency: "CNY" as Currency };

export function WorkspacesPage() {
  const workspaces = useAsync(() => api<{ items: EnterpriseWorkspace[] }>("/workspaces"), []);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const items = workspaces.data?.items ?? [];
  const connectedCount = useMemo(() => items.filter((item) => item.feishu_tenant_name || item.feishu_tenant_key).length, [items]);
  const appCount = useMemo(() => items.reduce((total, item) => total + Number(item.app_count || 0), 0), [items]);

  const openCreate = () => {
    setForm(blankForm);
    setError("");
    setMessage("");
    setCreating(true);
  };

  const closeCreate = () => {
    if (busy) return;
    setCreating(false);
    setError("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api<EnterpriseWorkspace>("/workspaces", {
        method: "POST",
        body: json({
          name: form.name,
          legal_name: form.legal_name || null,
          currency: form.currency,
        }),
      });
      setCreating(false);
      setForm(blankForm);
      setMessage("企业工作区已创建");
      await workspaces.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "创建工作区失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-shell workspace-page">
      <header className="page-heading split">
        <div>
          <span>ENTERPRISE WORKSPACES</span>
          <h1>企业工作区</h1>
          <p>工作区只归属当前平台用户，并与一个飞书企业绑定；其他平台用户不能加入，也不能使用该工作区下的应用。</p>
        </div>
        <button className="primary-button" onClick={openCreate}>
          <Plus size={16} />
          新建工作区
        </button>
      </header>

      {message && <Success>{message}</Success>}
      {!creating && error && <Failure>{error}</Failure>}

      <section className="metric-grid">
        <Metric icon={<Building2 />} label="我的工作区" value={`${items.length}`} />
        <Metric icon={<Link2 />} label="飞书企业连接" value={`${connectedCount}`} />
        <Metric icon={<WalletCards />} label="企业应用" value={`${appCount}`} />
      </section>

      <section className="admin-panel">
        <header>
          <div>
            <span>OWNED BY CURRENT USER</span>
            <h2>工作区列表</h2>
          </div>
          <button className="secondary-button" onClick={() => void workspaces.retry()}>
            <RefreshCw size={15} />
            刷新
          </button>
        </header>

        {workspaces.loading ? (
          <Loading label="正在加载企业工作区" />
        ) : workspaces.error ? (
          <ErrorState error={workspaces.error} retry={workspaces.retry} />
        ) : !items.length ? (
          <Empty title="还没有企业工作区" detail="创建工作区后，才能为自己的企业绑定飞书并创建 AI 应用。" />
        ) : (
          <div className="record-list">
            {items.map((workspace) => (
              <WorkspaceRow key={workspace.id} workspace={workspace} />
            ))}
          </div>
        )}
      </section>

      <section className="admin-panel">
        <header>
          <div>
            <span>ACCESS BOUNDARY</span>
            <h2>工作区边界</h2>
          </div>
          <Badge tone="accent">单用户所有权</Badge>
        </header>
        <div className="detail-list">
          <article>
            <strong>飞书企业绑定</strong>
            <span>一个工作区对应一个飞书企业，应用继承该连接并由 OPC 托管凭证。</span>
          </article>
          <article>
            <strong>平台用户隔离</strong>
            <span>工作区不会开放成员加入，其他平台账号不能查看、创建或运行这里的应用。</span>
          </article>
          <article>
            <strong>应用入口</strong>
            <span>先从产品目录选择产品，再在我的应用中查看运行、监控、审批和用量。</span>
          </article>
        </div>
      </section>

      {creating && (
        <div className="modal-layer">
          <button className="modal-backdrop" aria-label="关闭" onClick={closeCreate} />
          <form className="modal-card" onSubmit={submit}>
            <header>
              <h2>新建企业工作区</h2>
              <button type="button" onClick={closeCreate} aria-label="关闭">
                <X size={16} />
              </button>
            </header>
            {error && <Failure>{error}</Failure>}
            <Field label="工作区名称" required>
              <Input
                required
                maxLength={80}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="例如：简行科技工作区"
              />
            </Field>
            <Field label="法定名称">
              <Input
                maxLength={120}
                value={form.legal_name}
                onChange={(event) => setForm({ ...form, legal_name: event.target.value })}
                placeholder="例如：上海简行科技有限公司"
              />
            </Field>
            <Field label="结算币种" required>
              <Select value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value as Currency })}>
                <option value="CNY">CNY</option>
                <option value="USD">USD</option>
              </Select>
            </Field>
            <button className="primary-button" disabled={busy}>
              {busy ? "创建中..." : "创建工作区"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function WorkspaceRow({ workspace }: { workspace: EnterpriseWorkspace }) {
  const connected = Boolean(workspace.feishu_tenant_name || workspace.feishu_tenant_key);

  return (
    <article>
      <div>
        <strong>{workspace.name}</strong>
        <small>
          {workspace.legal_name || "未填写法定名称"} · {workspace.currency} · 创建于 {formatDate(workspace.created_at)}
        </small>
      </div>
      <Badge tone={workspace.status === "active" ? "success" : workspace.status === "pending" ? "warning" : "danger"}>
        {workspaceStatus(workspace.status)}
      </Badge>
      <span>{connected ? workspace.feishu_tenant_name || "飞书已连接" : "待绑定飞书"}</span>
      <span>{Number(workspace.app_count || 0)} 个应用</span>
      <div className="row-actions">
        <Link className="secondary-button compact" to="/products">
          <Store size={14} />
          产品目录
        </Link>
        <Link className="primary-button compact" to="/apps">
          <FolderKanban size={14} />
          我的应用
        </Link>
      </div>
    </article>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <article>
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </article>
  );
}

function workspaceStatus(status: EnterpriseWorkspace["status"]) {
  return { active: "有效", pending: "待确认", suspended: "已暂停" }[status] || status;
}
